/**
 * RootLayout — 모든 라우트를 감싸는 최상위 레이아웃.
 *
 * Router context 안에서 globally-mounted 싱글턴들을 같이 마운트.
 * - ActivityHost: SubActivity 스택 렌더 (kuji/detail, terms/view, 등).
 * - AddressFormPopup: 어느 페이지에서든 `useAddressForm().open({...})` 호출 시 표시.
 *   라우트 기반이 아니라 store 기반이라 호출 페이지가 unmount 되지 않음.
 * - Deep link → router navigate: native(Capacitor) 환경에서 universal/app link
 *   또는 peeka:// custom scheme 으로 앱 진입 시 React Router 의 navigate 로 path 전달.
 *
 * RouterProvider 의 children 은 렌더되지 않으므로, 전역 컴포넌트는 Route 트리 안에서
 * 렌더해야 useNavigate 같은 hook 이 안전하게 동작.
 */
import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import ActivityHost from "./ActivityHost";
import SystemNavScrim from "./SystemNavScrim";
import AppSplash from "../splash/AppSplash";
import { EditionProvider } from "../edition/EditionContext";
import { AppBarSlotProvider } from "./AppBarSlotContext";
import { ToastProvider } from "../feedback/Toast";
import ExitConfirmDialog from "../feedback/ExitConfirmDialog";
import PushPermissionGate from "../feedback/PushPermissionGate";
import UpdatePrompt from "../update/UpdatePrompt";
import UpdatingOverlay from "../update/UpdatingOverlay";
import { useDeepLink } from "../../native/app";
import { closeAppleBrowser } from "../../native/socialAuth/apple";
import { env } from "../../config/env";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { useFcmPushLifecycle } from "../../native/fcmPush";
import { ensurePushPermission } from "../../native/pushPermission";
import { isNative, flushNativeSafeArea } from "../../native";
import { bootstrapAuth } from "../../api/client";
import { fetchMe } from "../../api/member";
import { useDeepLinkReplay } from "../../hooks/useDeepLinkReplay";
import { useGuestScanTransfer } from "../../hooks/useGuestScanTransfer";
import { usePopstateSync } from "../../navigation/usePopstateSync";
import { useHardwareBackHandler } from "../../navigation/useHardwareBackHandler";
import { useActivityHydration } from "../../navigation/useActivityHydration";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useActivityStore } from "../../store/activityStore";
import { useAuthStore } from "../../store/authStore";
import { useSessionContinuity } from "../../hooks/useSessionContinuity";
import { useScreenTracking } from "../../hooks/useScreenTracking";
import { useNotificationsQuery } from "../../hooks/useNotificationQueries";
import {
  isOtaApplyingReadyPending,
  subscribeOtaApplyingReadyPending,
} from "../../native/otaApplyingState";


/**
 * App Link / Universal Link 호스트 — 환경별 분기 (dev=dev.peeka.app, prod=peeka.app).
 * VITE_GIFT_LINK_DOMAIN(.env.{dev,prod}) 로 주입 — share.ts / AndroidManifest giftAppLinkHost /
 * capacitor.config hostname 과 동일 소스. 미설정 시 dev 도메인 fallback.
 * universal/app link 의 https://<HOST>/<path> 가 들어오면 path 만 router 로 navigate.
 */
/**
 * 앱 custom scheme 의 URL protocol 형태 (예: "peeka:").
 * AndroidManifest 의 <data android:scheme> · iOS Info.plist CFBundleURLSchemes 와 반드시 일치해야 한다.
 */
const APP_SCHEME_PROTOCOL = `${env.VITE_APP_DEEP_LINK_SCHEME}:`;

const APP_LINK_HOSTNAME =
  (import.meta.env.VITE_GIFT_LINK_DOMAIN as string | undefined)?.trim() ||
  "peeka.app";

// 첫 진입 시 비로그인 사용자에게 알림 권한을 1회만 묻기 위한 세션 가드.
// (로그인 사용자는 로그인 흐름의 setupAfterLogin 가 권한을 처리하므로 여기선 skip.)
let firstAccessAsked = false;

export default function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const activityNav = useActivityNavigator();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [otaReadyPending, setOtaReadyPending] = useState(() => isOtaApplyingReadyPending());

  useEffect(() => {
    const refresh = () => setOtaReadyPending(isOtaApplyingReadyPending());
    refresh();
    const unsubscribe = subscribeOtaApplyingReadyPending(refresh);
    const ttlPoll = window.setInterval(refresh, 1000);
    return () => {
      unsubscribe();
      window.clearInterval(ttlPoll);
    };
  }, []);

  // iOS WKWebView safe-area desync 보정 — SPA 라우트 전환 때마다 네이티브 flush 요청.
  // (Apple 로그인 시트/인앱 브라우저 복귀 후 "홈으로 navigate" 등에서 AppBar 가 노치에
  //  붙는 재발 방지. 네이티브 MainViewController 가 WKScriptMessageHandler 로 수신.)
  // 콜드런치/리로드는 네이티브가 자체 처리하므로, 여기선 path 변화에만 반응한다.
  useEffect(() => {
    flushNativeSafeArea();
  }, [location.pathname]);

  // 콜드스타트 부트스트랩 — access 메모리 소멸 후 /auth/refresh 1회로 세션 복구.
  // RequireAuth 가 비로그인으로 오판해 로그인 화면을 띄우지 않도록, 부트스트랩 settle 까지
  // 라우트 렌더를 보류한다. BE 느림/다운 대비 3s 안전망(§13) — 이후엔 그냥 진행.
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    let settled = false;
    const finish = () => {
      if (!settled) {
        settled = true;
        useAuthStore.getState().markBootstrapped();
        setAuthReady(true);
      }
    };
    const timer = setTimeout(finish, 3000);
    bootstrapAuth()
      .then((ok) => {
        // 토큰만 복구되고 user 는 메모리 소멸 상태 — Me 조회로 복구 (authStore 주석의
        // "부트스트랩/Me 조회로 복구" 담당). authReady 를 막지 않도록 fire-and-forget.
        if (!ok || useAuthStore.getState().user) return;
        void fetchMe()
          .then((me) => {
            if (me && !useAuthStore.getState().user) {
              useAuthStore.getState().setUser(me);
            }
          })
          .catch(() => {});
      })
      .catch(() => {})
      .finally(() => {
        clearTimeout(timer);
        finish();
      });
    return () => clearTimeout(timer);
  }, []);

  // Phase 2B: keep activity store in sync with router URL changes that did
  // not originate from useActivityNavigator (browser/hardware back, legacy
  // navigate() calls, deep links). No-op when store is empty.
  usePopstateSync();

  // Phase 4: route Capacitor Android hardware back through the back resolver
  // (priority: dialog → entry → activity → main → exit). No-op on web.
  useHardwareBackHandler();

  // PoC: deep-link 으로 sub-activity URL 직접 진입 시 store 자동 hydrate.
  // (브라우저 주소창에 /shop/1 입력 → store 가 비었어도 ActivityHost 가 렌더하도록)
  useActivityHydration();

  // OS 가 백그라운드 프로세스 kill 후 사용자가 다시 진입했을 때 AppSplash 가
  // "이어서 쓰는 중" 으로 판단하고 애니메이션을 skip 하도록, 활동 흔적을 localStorage 에 남김.
  useSessionContinuity();

  // GA4 전 화면 자동 screen_view — 라우트(pathname) 변경마다 1회 전송.
  // SubActivity 도 URL 을 바꾸므로 함께 커버 (hooks/useScreenTracking.ts).
  useScreenTracking();

  // 푸시 리스너 bootstrap + 로그인 시 기기 등록(FCM/APNs 토큰 → BE).
  // 권한 요청은 로그인 직후에만 (bootstrap 단계에서는 X).
  useFcmPushLifecycle();

  // Killed cold-boot 시 알림 탭이 authStore hydration 보다 빠른 race 해결.
  // pendingDeepLink 큐잉 → hydration 완료 시 consume + navigate.
  useDeepLinkReplay();
  // §4.3 로그인 시 비회원 스캔 기록 → 계정 이전
  useGuestScanTransfer();

  // 알림 인박스 prefetch — root 레벨에서 1회 호출. enabled:isAuthed 로 비로그인 시 noop.
  // AppBar 뱃지(useHasUnreadNotifications) / NotificationCenterPage(useNotificationsQuery) /
  // HomePage 가 동일 cache 공유 → 어디든 진입 시 이미 데이터 준비됨.
  useNotificationsQuery();

  // 첫 진입(비로그인) 시 알림 권한을 1회만 묻는다. 로그인 사용자는 registerPushDevice 가
  // 처리하므로 여기선 skip. nag 방지 위해 settings 프롬프트는 띄우지 않음.
  useEffect(() => {
    if (firstAccessAsked) return;
    firstAccessAsked = true;
    if (!accessToken && isNative()) {
      void ensurePushPermission({ allowSettingsPrompt: false });
    }
    // 마운트 시 1회만 — accessToken 변화에 재실행하지 않도록 의존성 비움.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // dev-only: expose nav helpers on window for Claude preview verification.
  const overlayNavigate = useOverlayNavigate();
  useEffect(() => {
    if (import.meta.env.DEV) {
      const w = window as unknown as Record<string, unknown>;
      w.__navigate = navigate;
      w.__overlayNavigate = overlayNavigate;
      w.__activityStore = useActivityStore;
      w.__authStore = useAuthStore;
    }
  }, [navigate, overlayNavigate]);

  const handleDeepLink = useCallback(
    (url: URL) => {
      // Apple 웹/Android 로그인 네이티브 복귀 — Apple 은 SDK 부재로 Custom Tab 웹 OAuth 를 쓰며,
      // BE web-callback 이 state 의 native 마커를 보고 peeka://oauth/apple/callback 로 302 회신한다.
      // Custom Tab 을 닫고 인앱 콜백 라우트로 보내 OAuthCallbackPage 가 code→로그인/가입을 처리한다
      // (CSRF state 는 메인 WebView 의 sessionStorage/쿠키에 남아 검증 통과).
      if (
        url.protocol === APP_SCHEME_PROTOCOL &&
        url.host === "oauth" &&
        url.pathname.startsWith("/apple/callback")
      ) {
        void closeAppleBrowser();
        const callbackUrl = `/oauth/apple/callback${url.search}`;
        const inAuth = useActivityStore
          .getState()
          .activities.some((a) => a.kind === "auth" || a.kind === "auth/signup");
        if (inAuth) {
          activityNav.replaceEntry({ url: callbackUrl });
        } else {
          // 콜드복귀(앱이 백그라운드에서 정리된 경우) — 새 auth activity 로 콜백을 띄운다.
          activityNav.open({
            url: callbackUrl,
            kind: "auth",
            presentation: "fullscreen",
            appBarAction: "close",
            fallbackPath: "/",
          });
        }
        return;
      }

      // Google 은 네이티브 SDK 플러그인으로 처리 — 그 외 oauth 딥링크는 skip.
      if (url.protocol === APP_SCHEME_PROTOCOL && url.host === "oauth") return;

      let target: string | null = null;
      if (url.protocol === APP_SCHEME_PROTOCOL) {
        // peeka://gift/receive?token=... → /gift/receive?token=...
        target = `/${url.host}${url.pathname}${url.search}`;
      } else if (
        (url.protocol === "https:" || url.protocol === "http:") &&
        url.host === APP_LINK_HOSTNAME
      ) {
        target = `${url.pathname}${url.search}`;
      }

      if (target) {
        navigate(target, { replace: true });
      }
    },
    [navigate, activityNav],
  );

  useDeepLink(handleDeepLink);

  // 부트스트랩 settle 전에는 라우트 렌더 보류 (네이티브 splash 가 가림 / 웹은 짧은 공백).
  if (!authReady) return otaReadyPending ? <UpdatingOverlay /> : null;

  return (
    // 카드 에디션(언어판) 선택은 검색 진입에서 고른 값이 세트·카드 상세까지 이어져야
    // 하므로 라우트 트리 전체를 감싼다 (화면정의서 SC-009 / IA v0.1 rev 진입 플로우 개정).
    <EditionProvider>
        <AppBarSlotProvider>
        <ToastProvider>
          <Outlet />
          <ActivityHost />
          {/* 하단 시스템 메뉴 영역(제스처바) 유리 마감 — 전 화면 공통, 스캔 라이브 제외. */}
          <SystemNavScrim />
          <ExitConfirmDialog />
          {/* 알림 권한 거부 시 "설정으로 이동" 확인 — 앱 어디서든 동작하도록 전역 마운트. */}
          <PushPermissionGate />
          {/* OTA 업데이트 안내/강제/적용중 오버레이 — 라우터+ToastProvider 안이어야 back 바인딩·토스트 동작. */}
          <UpdatePrompt />
          {otaReadyPending ? <UpdatingOverlay /> : null}
          <AppSplash />
        </ToastProvider>
        </AppBarSlotProvider>
    </EditionProvider>
  );
}
